import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsage } from '../context/UsageContext';
import { useFreemium } from '../context/FreemiumContext';
import IAPaywall from '../components/IAPaywall';
import { draftsAPI, exportAPI } from '../lib/api';
import useAutoSave from '../hooks/useAutoSave';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { cn, formatDate, getLevelColor, downloadFile, TAREAS, MODULOS } from '../lib/utils';import NivelEstimadoBadge from '../components/NivelEstimadoBadge';
import { NIVELES_DOCENTE, getDiagnostico } from '../lib/diagnostico-data';
import {
    getModulosParvulariaSelectItems,
    getTareasParvulariaPorModulo,
    getIndicadoresParvulariaPorTarea,
    nivelEsParvularia,
} from '../lib/parvularia-data';
import ContextoPedagogicoBadge from '../components/ContextoPedagogicoBadge';
import IndicadorImpactBanner from '../components/IndicadorImpactBanner';
import BorradoresEmptyState from '../components/BorradoresEmptyState';
import RedaccionIAPanel from '../components/RedaccionIAPanel';
import {
    FileText,
    Plus,
    Loader2,
    ChevronRight,
    Save,
    Sparkles,
    Download,
    Trash2,
    CheckCircle2,
    AlertCircle,
    TrendingUp,
    BarChart3,
    ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { trackFunnelEvent } from '../lib/funnel-tracker';

/**
 * Componente auxiliar: cuando la combinación nivel+indicador no tiene diagnóstico,
 * se muestra al usuario un fallback amigable y se registra el evento para detectar
 * usuarios atascados en este paso (problema conocido: docentes que no logran avanzar).
 */
function NoDiagnosticoStuck({ nivel, indicadorId, userId, onContinuar }) {
    useEffect(() => {
        trackFunnelEvent('borrador_no_diagnostico', {
            user_id: userId,
            last_action: `nivel=${nivel}|indicador=${indicadorId}`,
            error: 'sin_datos_diagnostico',
        });
    }, [nivel, indicadorId, userId]);
    return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center text-sm text-amber-800 space-y-3" data-testid="borrador-no-diagnostico">
            <div>
                <p className="font-medium">No hay datos de diagnóstico para esta combinación.</p>
                <p className="mt-1 text-xs text-amber-700">
                    Puedes continuar de todas formas — los datos nacionales se actualizan periódicamente.
                </p>
            </div>
            <Button
                className="w-full gap-2"
                onClick={onContinuar}
                data-testid="continuar-sin-diagnostico-btn"
            >
                <ArrowRight className="h-4 w-4" />
                Continuar y comenzar borrador
            </Button>
        </div>
    );
}

export default function Borradores() {
    const { draftId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { profile } = useAuth();
    const { puedeUsarIA, refresh: refreshUsage, improveWritingUnlimited, improveWritingCredits } = useUsage();
    const {
        freeIaCredits, planActivo: freemiumPlanActivo,
        unlimitedAccess, isAdmin: freemiumIsAdmin, openModal: openFreemiumModal,
        refresh: refreshFreemium,
    } = useFreemium();
    const [loading, setLoading] = useState(true);const [drafts, setDrafts] = useState([]);
    const [currentDraft, setCurrentDraft] = useState(null);
    const [showNewDialog, setShowNewDialog] = useState(false);
    const [newDraftData, setNewDraftData] = useState({ nivel: '', modulo: '', tarea: '', indicador: '', indicadorId: '', contenido: '', titulo: '' });
    const [diagnosticoVisible, setDiagnosticoVisible] = useState(false);
    const [borradorHabilitado, setBorradorHabilitado] = useState(false);
    const [saving, setSaving] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);

    // Autosave del contenido del borrador (debounce 8s + blur + beforeunload)
    const autosaveFn = async (contenido) => {
        if (!currentDraft?.id) return;
        await draftsAPI.autosave(currentDraft.id, contenido);
    };
    const autoSave = useAutoSave(currentDraft?.contenido || '', autosaveFn, {
        enabled: !!currentDraft?.id,
        debounceMs: 8000,
    });

    useEffect(() => {
        loadDrafts();
        // Tracking: entrada al módulo Borradores
        trackFunnelEvent('borradores_opened', {
            user_id: profile?.id,
            last_action: draftId ? 'open_specific_draft' : 'list_view',
        });
    }, [draftId, profile?.id]);

    // Si el docente llega desde Tareas/Rúbricas con ?tarea=...&indicador=..., abrir
    // el modal con esos valores preseleccionados para evitar fricción.
    useEffect(() => {
        if (draftId) return; // estamos viendo un borrador específico, no aplicar
        const tareaParam = searchParams.get('tarea');
        const indicadorParam = searchParams.get('indicador');
        if (!tareaParam || !indicadorParam) return;
        // Resolver tarea + indicador a partir de los ids del query string
        let tareaMatch = null;
        let indicadorMatch = null;
        for (const m of MODULOS) {
            const t = m.tareas.find((x) => x.id === tareaParam);
            if (!t) continue;
            const i = t.indicadores.find((y) => y.id === indicadorParam);
            if (i) {
                tareaMatch = t;
                indicadorMatch = i;
                break;
            }
        }
        if (tareaMatch && indicadorMatch) {
            setNewDraftData((prev) => ({
                ...prev,
                tarea: tareaMatch.id,
                indicador: indicadorMatch.nombre,
                indicadorId: indicadorMatch.id,
            }));
            setShowNewDialog(true);
        }
    }, [draftId, searchParams]);

    useEffect(() => {
        if (draftId) {
            loadDraft(draftId);
        } else {
            setCurrentDraft(null);
        }
    }, [draftId]);

    const loadDrafts = async () => {
        try {
            const response = await draftsAPI.getAll();
            setDrafts(response.data);
        } catch (error) {
            console.error('Error loading drafts:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadDraft = async (id) => {
        setLoading(true);
        try {
            const response = await draftsAPI.get(id);
            setCurrentDraft(response.data);
        } catch (error) {
            console.error('Error loading draft:', error);
            toast.error('Error al cargar el borrador');
            navigate('/borradores');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateDraft = async () => {
        if (!newDraftData.tarea || !newDraftData.indicador || !newDraftData.contenido) {
            toast.error('Por favor completa todos los campos');
            trackFunnelEvent('borrador_creation_failed', {
                user_id: profile?.id,
                last_action: 'missing_fields',
                error: 'campos_incompletos',
            });
            return;
        }

        trackFunnelEvent('borrador_creation_attempted', {
            user_id: profile?.id,
            last_action: `nivel=${newDraftData.nivel}|tarea=${newDraftData.tarea}|indicador=${newDraftData.indicadorId}`,
        });

        setSaving(true);
        try {
            const isFirst = drafts.length === 0;
            // Para Parvularia: enviar `modulo` explícito y nivel_docente="Educación Parvularia"
            // para que el backend seleccione los prompts aislados de Parvularia.
            const isPv = nivelEsParvularia(newDraftData.nivel);
            const draftPayload = {
                tarea: newDraftData.tarea,
                indicador: newDraftData.indicador,
                contenido: newDraftData.contenido,
                titulo: newDraftData.titulo || null,
                nivel_docente: isPv ? 'Educación Parvularia' : newDraftData.nivel,
                indicador_id: newDraftData.indicadorId,
                dificultad: currentDiagnostico?.clasificacion || null,
                ...(isPv && newDraftData.modulo ? { modulo: newDraftData.modulo } : {}),
                ...(isPv ? { modalidad: 'parvularia' } : {}),
            };
            const response = await draftsAPI.create(draftPayload);
            setDrafts([response.data, ...drafts]);
            setShowNewDialog(false);
            resetNewDraftDialog();
            trackFunnelEvent('borrador_created', {
                user_id: profile?.id,
                last_action: `indicador=${newDraftData.indicadorId}${isFirst ? '|first_draft' : ''}`,
            });
            navigate(`/borradores/${response.data.id}`);
            if (isFirst) {
                toast.success('🎉 ¡Excelente! Ya creaste tu primer borrador', {
                    description: 'Ahora puedes analizarlo con IA y comenzar a mejorarlo.',
                    duration: 6000,
                });
            } else {
                toast.success('Borrador creado');
            }
        } catch (error) {
            console.error('Error creating draft:', error);
            const errMsg = error?.response?.data?.detail || error?.message || 'unknown';
            trackFunnelEvent('borrador_creation_failed', {
                user_id: profile?.id,
                last_action: `status=${error?.response?.status || 'network'}`,
                error: String(errMsg).slice(0, 200),
            });
            toast.error('Error al crear el borrador');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDraft = async () => {
        if (!currentDraft) return;

        setSaving(true);
        try {
            await draftsAPI.update(currentDraft.id, {
                tarea: currentDraft.tarea,
                indicador: currentDraft.indicador,
                contenido: currentDraft.contenido,
            });
            loadDrafts();
            toast.success('Borrador guardado');
        } catch (error) {
            console.error('Error saving draft:', error);
            toast.error('Error al guardar el borrador');
        } finally {
            setSaving(false);
        }
    };

    const handleImproveWriting = async () => {
        if (!currentDraft) return;

        // Requisito previo P0: la IA nunca genera la tarea desde cero.
        const palabras = (currentDraft.contenido || '').trim().split(/\s+/).filter(Boolean).length;
        if (palabras < 25 || (currentDraft.contenido || '').trim().length < 150) {
            toast.warning('Primero escribe tu tarea', {
                description: 'La IA trabaja a partir de tus ideas, experiencias y decisiones profesionales; no reemplaza tu autoría.',
                duration: 6000,
            });
            return;
        }

        // Oct 2026: contador DEDICADO `improve_writing_credits`. Si el usuario
        // está exento (admin/unlimited/plan/grant) el backend lo detecta y
        // devuelve `unlimited: true` sin descontar nada.
        setAnalyzing(true);
        try {
            const response = await draftsAPI.improveWriting(currentDraft.id);
            if (response.data?.redaccion_ia) {
                setCurrentDraft({ ...currentDraft, redaccion_ia: response.data.redaccion_ia });
            }
            trackFunnelEvent('redaccion_ia_completada', {
                user_id: profile?.id,
                last_action: 'improve_writing',
                meta: { draft_id: currentDraft.id, indicador_id: currentDraft.indicador_id },
            });
            const remaining = response.data?.improve_writing_credits_remaining;
            if (response.data?.unlimited) {
                toast.success('Propuestas de redacción listas.');
            } else if (typeof remaining === 'number') {
                toast.success(`Propuestas de redacción listas. Te ${remaining === 1 ? 'queda 1 mejora gratuita' : `quedan ${remaining} mejoras gratuitas`}.`);
            } else {
                toast.success('Propuestas de redacción listas.');
            }
            if (refreshFreemium) refreshFreemium();
            if (refreshUsage) refreshUsage();
        } catch (error) {
            console.error('Error improving writing:', error);
            if (error?.response?.status === 402 && error?.response?.data?.detail?.code === 'sin_mejoras_ia') {
                // El interceptor del FreemiumContext abre el modal paywall con copy correcto.
                if (refreshUsage) refreshUsage();
                if (refreshFreemium) refreshFreemium();
            } else if (error?.response?.status === 400 && error?.response?.data?.detail?.code === 'contenido_insuficiente') {
                toast.warning('Primero escribe tu tarea', {
                    description: error.response.data.detail.mensaje,
                    duration: 6000,
                });
            } else {
                toast.error('Error al mejorar la redacción');
            }
        } finally {
            setAnalyzing(false);
        }
    };

    const handleAnalyzeDraft = async () => {
        if (!currentDraft) return;

        // Regla Feb 2026: Si el usuario no es admin/unlimited y no tiene plan activo,
        // y su saldo de créditos IA está en 0, mostrar paywall en lugar de ejecutar.
        const exento = freemiumIsAdmin || unlimitedAccess || freemiumPlanActivo;
        if (!exento && (freeIaCredits === 0)) {
            openFreemiumModal({
                code: 'sin_creditos_ia',
                titulo: 'Has utilizado tus créditos IA gratuitos',
                mensaje: 'Tu trabajo permanece guardado. Activa el Plan Plataforma para seguir analizando borradores con IA.',
                free_ia_credits: 0,
                cta_planes: '/activar-plan',
                provider: 'mercado_pago',
            });
            return;
        }

        setAnalyzing(true);
        try {
            const response = await draftsAPI.analyze(currentDraft.id);
            setCurrentDraft(response.data);
            loadDrafts();
            trackFunnelEvent('analysis_completed', {
                user_id: profile?.id,
                last_action: 'analyze_draft',
                meta: { draft_id: currentDraft.id, indicador_id: currentDraft.indicador_id },
            });
            toast.success('Análisis completado');
            if (refreshFreemium) refreshFreemium();
        } catch (error) {
            console.error('Error analyzing draft:', error);
            if (error?.response?.status === 402) {
                // El interceptor de FreemiumContext ya abrirá el modal.
                if (refreshUsage) refreshUsage();
                if (refreshFreemium) refreshFreemium();
            } else if (error?.response?.status === 422 && error?.response?.data?.detail?.code === 'parvularia_sin_rubrica') {
                const d = error.response.data.detail;
                const lista = (d.rubricas_disponibles || []).map((r) => `${r.codigo} ${r.nombre}`).slice(0, 8).join(' · ');
                toast.error(d.message, { description: `Rúbricas disponibles: ${lista}…`, duration: 12000 });
                if (refreshFreemium) refreshFreemium();
            } else {
                toast.error('Error al analizar el borrador');
            }
        } finally {
            setAnalyzing(false);
        }
    };

    const handleDeleteDraft = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este borrador?')) return;

        try {
            await draftsAPI.delete(id);
            setDrafts((prev) => prev.filter((d) => d.id !== id));
            if (currentDraft?.id === id) {
                navigate('/borradores');
            }
            toast.success('Borrador eliminado');
        } catch (error) {
            console.error('Error deleting draft:', error);
            toast.error('Error al eliminar el borrador');
        }
    };

    const handleExportFeedback = async () => {
        if (!currentDraft?.feedback) return;

        try {
            const response = await exportAPI.feedback(currentDraft.id);
            downloadFile(response.data.content, response.data.filename);
            toast.success('Retroalimentación exportada');
        } catch (error) {
            console.error('Error exporting feedback:', error);
            toast.error('Error al exportar');
        }
    };

    const handleExportDraft = async () => {
        if (!currentDraft) return;

        try {
            const response = await exportAPI.draft(currentDraft.id);
            downloadFile(response.data.content, response.data.filename);
            toast.success('Borrador exportado');
        } catch (error) {
            console.error('Error exporting draft:', error);
            toast.error('Error al exportar');
        }
    };

    const getIndicadoresForTarea = (tareaId) => {
        // Parvularia: buscar en el dataset pv-*
        if (nivelEsParvularia(newDraftData.nivel) || (tareaId && String(tareaId).startsWith('pv-'))) {
            return getIndicadoresParvulariaPorTarea(tareaId);
        }
        for (const modulo of MODULOS) {
            const tarea = modulo.tareas.find((t) => t.id === tareaId);
            if (tarea) return tarea.indicadores;
        }
        return [];
    };

    // Tareas visibles en el selector según nivel (y módulo si Parvularia)
    const getTareasVisibles = () => {
        if (nivelEsParvularia(newDraftData.nivel)) {
            if (!newDraftData.modulo) return [];
            return getTareasParvulariaPorModulo(newDraftData.modulo);
        }
        return TAREAS;
    };

    const isParvularia = nivelEsParvularia(newDraftData.nivel);

    // Resuelve tareaId desde el nombre almacenado en el borrador ("Tarea 4" -> "tarea-4")
    // Si hay ambigüedad (nombre repetido entre módulos, ej. "Tarea 5" en M2 y M3),
    // usa el indicador_id para desambiguar correctamente.
    const resolverTareaIdDesdeNombre = (tareaNombre, indicadorId = null) => {
        const matches = [];
        for (const modulo of MODULOS) {
            const tarea = modulo.tareas.find((t) => t.nombre === tareaNombre);
            if (tarea) matches.push({ tarea, modulo });
        }
        if (matches.length === 0) return null;
        if (matches.length === 1) return matches[0].tarea.id;
        // Ambigüedad: usar indicador_id si lo conocemos
        if (indicadorId) {
            const found = matches.find((m) =>
                m.tarea.indicadores?.some((i) => i.id === indicadorId)
            );
            if (found) return found.tarea.id;
            // Si el indicador empieza con 'indicador-m3-' usar el match de módulo-3
            if (indicadorId.startsWith('indicador-m3-')) {
                const m3 = matches.find((m) => m.modulo.id === 'modulo-3');
                if (m3) return m3.tarea.id;
            }
        }
        return matches[0].tarea.id;
    };

    const currentDiagnostico = newDraftData.nivel && newDraftData.indicadorId
        ? getDiagnostico(newDraftData.nivel, newDraftData.indicadorId)
        : null;

    const handleShowDiagnostico = () => {
        if (newDraftData.nivel && newDraftData.indicadorId) {
            setDiagnosticoVisible(true);
            trackFunnelEvent('borrador_diagnostico_viewed', {
                user_id: profile?.id,
                last_action: `nivel=${newDraftData.nivel}|indicador=${newDraftData.indicadorId}`,
            });
        }
    };

    const handleHabilitarBorrador = () => {
        setBorradorHabilitado(true);
        trackFunnelEvent('borrador_continuar_clicked', {
            user_id: profile?.id,
            last_action: `indicador=${newDraftData.indicadorId}`,
        });
    };

    const resetNewDraftDialog = () => {
        setNewDraftData({ nivel: '', modulo: '', tarea: '', indicador: '', indicadorId: '', contenido: '', titulo: '' });
        setDiagnosticoVisible(false);
        setBorradorHabilitado(false);
    };

    // Draft detail view
    if (draftId && currentDraft) {
        return (
            <Layout>
                <div className="space-y-6 animate-fade-in">
                    {/* Header */}
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                                <button
                                    onClick={() => navigate('/borradores')}
                                    className="hover:text-slate-900"
                                >
                                    Borradores
                                </button>
                                <ChevronRight className="h-4 w-4" />
                                <span className="text-slate-900">{currentDraft.indicador}</span>
                            </div>
                            <h1 className="font-heading text-2xl font-bold text-slate-900">
                                {currentDraft.tarea} - {currentDraft.indicador}
                            </h1>
                            <p className="mt-1 text-sm text-slate-500">
                                Última actualización: {formatDate(currentDraft.updated_at)}
                            </p>
                            <ContextoPedagogicoBadge
                                perfil={profile?.perfil}
                                tareaId={resolverTareaIdDesdeNombre(currentDraft.tarea, currentDraft.indicador_id)}
                                indicadorId={currentDraft.indicador_id}
                                indicadorNombre={currentDraft.indicador}
                                className="mt-3"
                            />
                            <IndicadorImpactBanner
                                indicadorId={currentDraft.indicador_id}
                                className="mt-3"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={handleExportDraft}
                                className="gap-2"
                                data-testid="export-draft-btn"
                            >
                                <Download className="h-4 w-4" />
                                Exportar
                            </Button>
                            <Button
                                onClick={handleSaveDraft}
                                disabled={saving}
                                className="gap-2"
                                data-testid="save-draft-btn"
                            >
                                {saving ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4" />
                                )}
                                Guardar
                            </Button>
                        </div>
                    </div>

                    <div className="grid gap-6 lg:grid-cols-2">
                        {/* Mi tarea — editor del docente */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="font-heading text-lg">Mi tarea</CardTitle>
                                <CardDescription>
                                    Escribe tu respuesta a partir de tu experiencia, decisiones pedagógicas y evidencias.
                                    Tu texto será la base sobre la cual trabajará el Asistente de Redacción con IA.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Textarea
                                    value={currentDraft.contenido}
                                    onChange={(e) =>
                                        setCurrentDraft({ ...currentDraft, contenido: e.target.value })
                                    }
                                    placeholder="Escribe o pega tu borrador aquí..."
                                    className="min-h-[300px] font-body"
                                    data-testid="draft-content"
                                />
                                <div
                                    className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[12px] text-slate-600"
                                    data-testid="autosave-banner"
                                >
                                    <span className={`inline-block h-2 w-2 rounded-full ${
                                        autoSave.status === 'saving' ? 'bg-amber-500 animate-pulse' :
                                        autoSave.status === 'saved' ? 'bg-emerald-500' :
                                        autoSave.status === 'error' ? 'bg-rose-500' : 'bg-slate-300'
                                    }`}></span>
                                    <span>
                                        {autoSave.status === 'saving' && 'Guardando…'}
                                        {autoSave.status === 'saved' && (
                                            <>Tu trabajo se guarda automáticamente. Puedes continuar y modificarlo cuando quieras.
                                                {autoSave.lastSavedAt && <span className="text-slate-400"> · {autoSave.lastSavedAt.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}</span>}
                                            </>
                                        )}
                                        {autoSave.status === 'idle' && 'Tu trabajo se guarda automáticamente. Puedes continuar y modificarlo cuando quieras.'}
                                        {autoSave.status === 'error' && 'No pudimos guardar el último cambio. Reintentando…'}
                                    </span>
                                </div>
                                {!puedeUsarIA && !unlimitedAccess && !freemiumIsAdmin && !freemiumPlanActivo && freeIaCredits === null && (
                                    <IAPaywall feature="Análisis estratégico de borrador" />
                                )}
                                {/* Contador de mejoras gratuitas (Oct 2026) */}
                                {!improveWritingUnlimited && typeof improveWritingCredits === 'number' && (
                                    <div
                                        className={`rounded-md border px-3 py-1.5 text-xs font-semibold text-center ${improveWritingCredits === 0 ? 'border-rose-200 bg-rose-50 text-rose-700' : improveWritingCredits === 1 ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-sky-200 bg-sky-50 text-sky-800'}`}
                                        data-testid="improve-writing-counter"
                                    >
                                        {improveWritingCredits === 0
                                            ? 'Has utilizado tus 3 mejoras gratuitas · Activa el Plan Plataforma para seguir'
                                            : improveWritingCredits === 1
                                                ? '⚡ Te queda 1 mejora gratuita'
                                                : `✨ Te quedan ${improveWritingCredits} mejoras gratuitas`}
                                    </div>
                                )}
                                {/* Paso 3 del circuito: mejorar redacción (acción principal) */}
                                <Button
                                    onClick={handleImproveWriting}
                                    disabled={analyzing || !currentDraft.contenido}
                                    className="w-full gap-2"
                                    data-testid="improve-writing-btn"
                                    aria-label={
                                        (freeIaCredits === 0 && !freemiumIsAdmin && !unlimitedAccess && !freemiumPlanActivo)
                                            ? 'Mejorar mi redacción con IA (requiere activar plan)'
                                            : 'Mejorar mi redacción con IA'
                                    }
                                >
                                    {analyzing ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Trabajando con tu texto...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="h-4 w-4" />
                                            ✨ Mejorar mi redacción con IA
                                        </>
                                    )}
                                </Button>
                                {/* Paso final del circuito: evaluar el texto ya editado (motor de análisis) */}
                                <Button
                                    variant="outline"
                                    onClick={handleAnalyzeDraft}
                                    disabled={analyzing || !currentDraft.contenido}
                                    className="w-full gap-2"
                                    data-testid="analyze-draft-btn"
                                    aria-label="Analizar con IA (evaluar mi borrador)"
                                >
                                    <BarChart3 className="h-4 w-4" />
                                    Analizar mi borrador con IA (evaluación)
                                </Button>
                                <p className="text-[11px] text-slate-400 text-center">
                                    Circuito sugerido: escribe → mejora la redacción → edita con tu juicio → evalúa el resultado.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Asistente de Redacción IA (panel principal derecho) */}
                        <RedaccionIAPanel
                            draft={currentDraft}
                            onDraftUpdated={(updated) => { setCurrentDraft(updated); loadDrafts(); }}
                            onRedaccionUpdated={(red) => setCurrentDraft((prev) => ({ ...prev, redaccion_ia: red }))}
                        />

                        {/* Evaluación del borrador (motor de análisis) — paso final del circuito */}
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle className="font-heading text-lg">Evaluación del borrador</CardTitle>
                                    <CardDescription>
                                        Después de editar tu versión final, comprueba cómo la interpreta el motor de evaluación según la rúbrica
                                    </CardDescription>
                                </div>
                                <NivelEstimadoBadge nivel={currentDraft.nivel_estimado} size="md" testid="borrador-nivel-badge" />
                            </CardHeader>
                            <CardContent>
                                {/* Histórico de análisis IA — visible cuando hay ≥2 análisis para mostrar movimientos */}
                                {(() => {
                                    const analyses = (currentDraft.activity_log || []).filter((a) => a.type === 'ai_analysis');
                                    if (analyses.length < 2) return null;
                                    // Contar movimientos entre niveles limítrofes
                                    const niveles = analyses.map((a) => a.nivel || '—');
                                    let mov = 0;
                                    for (let i = 1; i < niveles.length; i++) {
                                        if (niveles[i] !== niveles[i - 1]) mov++;
                                    }
                                    const levelColorMap = {
                                        'Destacado': 'bg-emerald-100 text-emerald-800 border-emerald-200',
                                        'Competente': 'bg-sky-100 text-sky-800 border-sky-200',
                                        'Básico': 'bg-amber-100 text-amber-800 border-amber-200',
                                        'Insatisfactorio': 'bg-rose-100 text-rose-800 border-rose-200',
                                        'Información insuficiente': 'bg-slate-100 text-slate-700 border-slate-200',
                                    };
                                    return (
                                        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="ia-analyses-history">
                                            <div className="flex items-center justify-between flex-wrap gap-2 mb-2.5">
                                                <div>
                                                    <h4 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                                                        🧠 Histórico de análisis IA
                                                        <span className="text-[10px] font-mono font-normal text-slate-500 bg-white border border-slate-200 rounded px-1.5 py-0.5">{analyses.length}</span>
                                                    </h4>
                                                    <p className="text-[11px] text-slate-500 mt-0.5">Movimientos entre niveles al re-analizar el mismo borrador.</p>
                                                </div>
                                                {mov > 0 && (
                                                    <span className="text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
                                                        ↔ {mov} {mov === 1 ? 'cambio' : 'cambios'} de nivel
                                                    </span>
                                                )}
                                            </div>
                                            <ol className="flex flex-wrap items-center gap-1.5" data-testid="ia-analyses-timeline">
                                                {analyses.map((a, i) => {
                                                    const nivel = a.nivel || '—';
                                                    const cls = levelColorMap[nivel] || 'bg-slate-100 text-slate-700 border-slate-200';
                                                    const changed = i > 0 && nivel !== (analyses[i - 1].nivel || '—');
                                                    const dateStr = (() => {
                                                        try { return new Date(a.at).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
                                                        catch { return ''; }
                                                    })();
                                                    return (
                                                        <React.Fragment key={i}>
                                                            {i > 0 && (
                                                                <span className={`text-xs ${changed ? 'text-amber-600 font-bold' : 'text-slate-400'}`}>→</span>
                                                            )}
                                                            <div className={`inline-flex flex-col items-start gap-0 rounded-md border px-2 py-1 ${cls} ${changed ? 'ring-2 ring-amber-300/60' : ''}`} title={dateStr}>
                                                                <span className="text-[11px] font-semibold leading-tight">{nivel}</span>
                                                                <span className="text-[9px] font-mono opacity-70 leading-tight">{dateStr}</span>
                                                            </div>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </ol>
                                            {mov > 0 && !currentDraft.feedback?.insufficient && (
                                                <p className="mt-2.5 text-[11px] text-slate-600 italic leading-snug">
                                                    Un cambio entre niveles al re-analizar sugiere una zona limítrofe: consulta el ícono ℹ️ junto a la calificación actual y comparte este caso en la próxima capacitación con la Docente Experta I.
                                                </p>
                                            )}
                                        </div>
                                    );
                                })()}

                                {currentDraft.feedback?.insufficient ? (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="feedback-insufficient">
                                        <div className="flex items-start gap-2">
                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800 text-lg">⚠️</div>
                                            <div>
                                                <h4 className="font-heading text-base font-semibold text-amber-900">
                                                    Información insuficiente para realizar una evaluación
                                                </h4>
                                                <p className="mt-1 text-sm text-amber-900">
                                                    Aún no es posible emitir una calificación confiable de este borrador.
                                                    La información proporcionada es demasiado breve para analizar
                                                    adecuadamente la calidad pedagógica de la planificación y entregar
                                                    una retroalimentación útil.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="rounded-md bg-white/70 border border-amber-100 p-3">
                                            <p className="text-xs font-semibold text-slate-800 mb-1.5">Te recomendamos incorporar:</p>
                                            <ul className="text-xs text-slate-700 space-y-1 pl-1">
                                                <li>• El objetivo de aprendizaje completo.</li>
                                                <li>• La actividad principal.</li>
                                                <li>• La estrategia metodológica.</li>
                                                <li>• La evidencia o producto esperado.</li>
                                                <li>• Los criterios de evaluación.</li>
                                                <li>• Cualquier otro elemento relevante de la planificación.</li>
                                            </ul>
                                        </div>
                                        <p className="text-[11px] italic text-slate-600">
                                            Una vez que el borrador contenga información suficiente, la IA realizará el
                                            análisis y emitirá una calificación fundamentada.
                                            {currentDraft.feedback?.content_stats && (
                                                <span className="block mt-1 text-slate-500 not-italic">
                                                    Actual: <strong>{currentDraft.feedback.content_stats.words}</strong> palabras
                                                    · Mínimo recomendado: {currentDraft.feedback.content_stats.min_words} palabras.
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                ) : currentDraft.feedback ? (
                                    <div className="space-y-6">
                                        {/* Fortalezas */}
                                        <div>
                                            <h4 className="flex items-center gap-2 font-semibold text-emerald-700 mb-2">
                                                <CheckCircle2 className="h-4 w-4" />
                                                Fortalezas
                                            </h4>
                                            <ul className="space-y-1.5">
                                                {currentDraft.feedback.fortalezas?.map((f, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                                        {f}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Brechas */}
                                        <div>
                                            <h4 className="flex items-center gap-2 font-semibold text-amber-700 mb-2">
                                                <AlertCircle className="h-4 w-4" />
                                                Brechas
                                            </h4>
                                            <ul className="space-y-1.5">
                                                {currentDraft.feedback.brechas?.map((b, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                                        {b}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Mejoras */}
                                        <div>
                                            <h4 className="flex items-center gap-2 font-semibold text-indigo-700 mb-2">
                                                <TrendingUp className="h-4 w-4" />
                                                Sugerencias de Mejora
                                            </h4>
                                            <ul className="space-y-1.5">
                                                {currentDraft.feedback.mejoras?.map((m, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                                                        {m}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Justificación */}
                                        {currentDraft.feedback.justificacion_nivel && (
                                            <div className="rounded-lg bg-slate-50 p-4">
                                                <h4 className="font-semibold text-slate-700 mb-2">
                                                    Justificación del Nivel
                                                </h4>
                                                <p className="text-sm text-slate-600">
                                                    {currentDraft.feedback.justificacion_nivel}
                                                </p>
                                            </div>
                                        )}

                                        <Button
                                            variant="outline"
                                            onClick={handleExportFeedback}
                                            className="w-full gap-2"
                                            data-testid="export-feedback-btn"
                                        >
                                            <Download className="h-4 w-4" />
                                            Exportar Retroalimentación
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <BarChart3 className="h-10 w-10 text-slate-300" />
                                        <p className="mt-4 text-slate-500 text-sm max-w-sm">
                                            Cuando tu texto esté listo, haz clic en
                                            <strong> “Analizar mi borrador con IA (evaluación)”</strong> para comprobar
                                            cómo el motor de evaluación interpreta tu versión final.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </Layout>
        );
    }

    // Drafts list view
    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-slate-900 md:text-3xl">
                            Mis Borradores
                        </h1>
                        <p className="mt-1 text-slate-500">
                            Guarda y trabaja tus respuestas para cada indicador
                        </p>
                    </div>
                    <Dialog open={showNewDialog} onOpenChange={(open) => {
                        setShowNewDialog(open);
                        if (open) {
                            trackFunnelEvent('new_draft_clicked', {
                                user_id: profile?.id,
                                last_action: 'dialog_opened',
                            });
                        }
                        if (!open) resetNewDraftDialog();
                    }}>
                        <DialogTrigger asChild>
                            <Button className="gap-2" data-testid="new-draft-btn">
                                <Plus className="h-4 w-4" />
                                Nuevo Borrador
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>Nuevo Borrador</DialogTitle>
                                <DialogDescription>
                                    {!diagnosticoVisible
                                        ? 'Selecciona tu nivel, tarea e indicador para ver el diagnóstico'
                                        : !borradorHabilitado
                                            ? 'Revisa el diagnóstico antes de comenzar tu borrador'
                                            : 'Escribe tu borrador considerando el diagnóstico'}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 pt-2">
                                {/* Step 1: Selects */}
                                <div className={cn(
                                    "grid gap-4",
                                    isParvularia ? "sm:grid-cols-4" : "sm:grid-cols-3"
                                )}>
                                    <div className="space-y-2">
                                        <Label>Nivel del Docente</Label>
                                        <Select
                                            value={newDraftData.nivel}
                                            onValueChange={(value) => {
                                                setNewDraftData({ ...newDraftData, nivel: value, modulo: '', tarea: '', indicador: '', indicadorId: '' });
                                                setDiagnosticoVisible(false);
                                                setBorradorHabilitado(false);
                                            }}
                                        >
                                            <SelectTrigger data-testid="new-draft-nivel">
                                                <SelectValue placeholder="Nivel" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {NIVELES_DOCENTE.map((n) => (
                                                    <SelectItem key={n.id} value={n.id}>
                                                        {n.abrev} - {n.nombre}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {isParvularia && (
                                        <div className="space-y-2">
                                            <Label>Módulo</Label>
                                            <Select
                                                value={newDraftData.modulo}
                                                onValueChange={(value) => {
                                                    setNewDraftData({ ...newDraftData, modulo: value, tarea: '', indicador: '', indicadorId: '' });
                                                    setDiagnosticoVisible(false);
                                                    setBorradorHabilitado(false);
                                                }}
                                            >
                                                <SelectTrigger data-testid="new-draft-modulo">
                                                    <SelectValue placeholder="Módulo" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {getModulosParvulariaSelectItems().map((m) => (
                                                        <SelectItem key={m.id} value={m.id}>
                                                            {m.nombre} — {m.titulo}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <div className="space-y-2">
                                        <Label>Tarea</Label>
                                        <Select
                                            value={newDraftData.tarea}
                                            onValueChange={(value) => {
                                                setNewDraftData({ ...newDraftData, tarea: value, indicador: '', indicadorId: '' });
                                                setDiagnosticoVisible(false);
                                                setBorradorHabilitado(false);
                                            }}
                                            disabled={isParvularia && !newDraftData.modulo}
                                        >
                                            <SelectTrigger data-testid="new-draft-tarea">
                                                <SelectValue placeholder="Tarea" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {getTareasVisibles().map((t) => (
                                                    <SelectItem key={t.id} value={t.id}>
                                                        {isParvularia ? `${t.nombre} — ${t.titulo}` : t.nombre}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Indicador</Label>
                                        <Select
                                            value={newDraftData.indicador}
                                            onValueChange={(value) => {
                                                const indicadores = getIndicadoresForTarea(newDraftData.tarea);
                                                const ind = indicadores.find((i) => i.nombre === value);
                                                setNewDraftData({ ...newDraftData, indicador: value, indicadorId: ind?.id || '' });
                                                setDiagnosticoVisible(false);
                                                setBorradorHabilitado(false);
                                                if (ind?.id && newDraftData.nivel && newDraftData.tarea) {
                                                    trackFunnelEvent('borrador_indicador_selected', {
                                                        user_id: profile?.id,
                                                        last_action: `nivel=${newDraftData.nivel}|tarea=${newDraftData.tarea}|indicador=${ind.id}`,
                                                    });
                                                }
                                            }}
                                            disabled={!newDraftData.tarea}
                                        >
                                            <SelectTrigger data-testid="new-draft-indicador">
                                                <SelectValue placeholder="Indicador" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {newDraftData.tarea &&
                                                    getIndicadoresForTarea(newDraftData.tarea).map((ind) => (
                                                        <SelectItem key={ind.id} value={ind.nombre}>
                                                            {ind.nombre}
                                                        </SelectItem>
                                                    ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Button: Ver diagnóstico */}
                                {!diagnosticoVisible && newDraftData.nivel && newDraftData.indicadorId && (
                                    <Button
                                        variant="outline"
                                        className="w-full gap-2"
                                        onClick={handleShowDiagnostico}
                                        data-testid="ver-diagnostico-btn"
                                    >
                                        <BarChart3 className="h-4 w-4" />
                                        Ver Diagnóstico del Indicador
                                    </Button>
                                )}

                                {/* Step 2: Diagnostic card */}
                                {diagnosticoVisible && currentDiagnostico && (
                                    <div className={cn(
                                        'rounded-xl border-2 p-5 space-y-4',
                                        currentDiagnostico.color === 'red' && 'border-rose-300 bg-rose-50/60',
                                        currentDiagnostico.color === 'amber' && 'border-amber-300 bg-amber-50/60',
                                        currentDiagnostico.color === 'green' && 'border-emerald-300 bg-emerald-50/60',
                                    )} data-testid="diagnostico-card">
                                        <div className="flex items-center gap-2">
                                            <BarChart3 className={cn(
                                                'h-5 w-5',
                                                currentDiagnostico.color === 'red' && 'text-rose-600',
                                                currentDiagnostico.color === 'amber' && 'text-amber-600',
                                                currentDiagnostico.color === 'green' && 'text-emerald-600',
                                            )} />
                                            <h3 className="font-heading font-bold text-slate-900">Diagnóstico del Indicador</h3>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div className="text-slate-500">Nivel:</div>
                                            <div className="font-medium text-slate-900">
                                                {NIVELES_DOCENTE.find((n) => n.id === newDraftData.nivel)?.nombre}
                                            </div>
                                            <div className="text-slate-500">Indicador:</div>
                                            <div className="font-medium text-slate-900 text-xs leading-snug">{newDraftData.indicador}</div>
                                        </div>

                                        {/* Percentage bars — bifurca formato Parvularia vs Legacy */}
                                        {currentDiagnostico.es_parvularia ? (
                                            <div className="space-y-2">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Resultados Nacionales 2025 · Parvularia</p>
                                                {[
                                                    { key: 'logrado_2025', label: 'Logrado', color: 'bg-emerald-500' },
                                                    { key: 'por_lograr_2025', label: 'Por lograr', color: 'bg-rose-500' },
                                                ].map(({ key, label, color }) => (
                                                    <div key={key} className="flex items-center gap-2">
                                                        <span className="w-28 text-xs text-slate-600">{label}</span>
                                                        <div className="flex-1 h-4 bg-white/60 rounded-full overflow-hidden">
                                                            <div
                                                                className={cn('h-full rounded-full transition-all', color)}
                                                                style={{ width: `${currentDiagnostico[key]}%` }}
                                                            />
                                                        </div>
                                                        <span className="w-14 text-right text-xs font-mono font-bold text-slate-700">
                                                            {currentDiagnostico[key]}%
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Resultados Nacionales</p>
                                                {[
                                                    { key: 'insatisfactorio', label: 'Insatisfactorio', color: 'bg-rose-400' },
                                                    { key: 'basico', label: 'Básico', color: 'bg-amber-400' },
                                                    { key: 'competente', label: 'Competente', color: 'bg-emerald-400' },
                                                    { key: 'destacado', label: 'Destacado', color: 'bg-indigo-400' },
                                                ].map(({ key, label, color }) => (
                                                    <div key={key} className="flex items-center gap-2">
                                                        <span className="w-28 text-xs text-slate-600">{label}</span>
                                                        <div className="flex-1 h-4 bg-white/60 rounded-full overflow-hidden">
                                                            <div
                                                                className={cn('h-full rounded-full transition-all', color)}
                                                                style={{ width: `${currentDiagnostico[key]}%` }}
                                                            />
                                                        </div>
                                                        <span className="w-12 text-right text-xs font-mono font-bold text-slate-700">
                                                            {currentDiagnostico[key]}%
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Difficulty badge */}
                                        <div className={cn(
                                            'flex items-center gap-3 rounded-lg border p-3',
                                            currentDiagnostico.color === 'red' && 'border-rose-300 bg-rose-100/60',
                                            currentDiagnostico.color === 'amber' && 'border-amber-300 bg-amber-100/60',
                                            currentDiagnostico.color === 'green' && 'border-emerald-300 bg-emerald-100/60',
                                        )}>
                                            <Badge className={cn(
                                                'text-xs font-bold px-3 py-1',
                                                currentDiagnostico.color === 'red' && 'bg-rose-600 text-white',
                                                currentDiagnostico.color === 'amber' && 'bg-amber-600 text-white',
                                                currentDiagnostico.color === 'green' && 'bg-emerald-600 text-white',
                                            )}>
                                                DIFICULTAD {currentDiagnostico.clasificacion}
                                            </Badge>
                                            <span className="text-xs text-slate-600">
                                                {currentDiagnostico.es_parvularia
                                                    ? `(${currentDiagnostico.por_lograr_2025}% de docentes 2025 aún por lograr este indicador)`
                                                    : `(${currentDiagnostico.dificultad}% en niveles Insuficiente + Básico)`}
                                            </span>
                                        </div>

                                        {/* Orientation */}
                                        <div className="rounded-lg bg-white/70 border border-slate-200 p-3">
                                            <p className="text-sm text-slate-700 leading-relaxed">{currentDiagnostico.orientacion}</p>
                                        </div>

                                        {/* Continue button */}
                                        {!borradorHabilitado && (
                                            <Button
                                                className="w-full gap-2"
                                                onClick={handleHabilitarBorrador}
                                                data-testid="continuar-borrador-btn"
                                            >
                                                <ArrowRight className="h-4 w-4" />
                                                Continuar y comenzar borrador
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {diagnosticoVisible && !currentDiagnostico && (
                                    <NoDiagnosticoStuck
                                        nivel={newDraftData.nivel}
                                        indicadorId={newDraftData.indicadorId}
                                        userId={profile?.id}
                                        onContinuar={handleHabilitarBorrador}
                                    />
                                )}

                                {/* Step 3: Content (only after diagnostic) */}
                                {borradorHabilitado && (
                                    <>
                                        {/* Nombre opcional del borrador (para reconocerlo fácilmente) */}
                                        <div className="space-y-2">
                                            <Label htmlFor="new-draft-titulo" className="text-sm">
                                                Nombre para identificar tu borrador <span className="text-slate-400 font-normal">(opcional)</span>
                                            </Label>
                                            <Input
                                                id="new-draft-titulo"
                                                value={newDraftData.titulo}
                                                onChange={(e) => setNewDraftData({ ...newDraftData, titulo: e.target.value })}
                                                placeholder="Ej: “Tarea 1 - Versión Inicial”, “Clase de Ciencias”…"
                                                data-testid="new-draft-titulo"
                                            />
                                        </div>

                                        {/* Caja lateral de tranquilidad */}
                                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3 sm:p-4 flex items-start gap-3" data-testid="draft-help-box">
                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500 text-white">
                                                <Sparkles className="h-4 w-4" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-xs sm:text-sm font-bold text-emerald-900">¿No sabes cómo comenzar?</p>
                                                <p className="mt-1 text-xs text-emerald-900/80 leading-relaxed">
                                                    No necesitas escribir una versión perfecta. Comienza con tus primeras ideas.
                                                    Luego podrás utilizar la IA para analizar tu trabajo y ayudarte a mejorarlo.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Contenido Inicial</Label>
                                            <Textarea
                                                value={newDraftData.contenido}
                                                onChange={(e) =>
                                                    setNewDraftData({ ...newDraftData, contenido: e.target.value })
                                                }
                                                placeholder="Escribe o pega tu borrador inicial..."
                                                rows={6}
                                                data-testid="new-draft-content"
                                                autoFocus
                                            />
                                        </div>
                                        <Button
                                            className="w-full h-12 text-base gap-2 bg-indigo-700 hover:bg-indigo-800"
                                            onClick={handleCreateDraft}
                                            disabled={saving}
                                            data-testid="create-draft-btn"
                                        >
                                            {saving ? (
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            ) : <Sparkles className="h-4 w-4" />}
                                            🚀 Comenzar a escribir
                                        </Button>
                                    </>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {loading ? (
                    <div className="flex h-64 items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                    </div>
                ) : drafts.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {drafts.map((draft) => (
                            <Card
                                key={draft.id}
                                className="card-hover cursor-pointer"
                                onClick={() => navigate(`/borradores/${draft.id}`)}
                            >
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            {draft.titulo && (
                                                <div className="text-xs font-bold text-indigo-700 mb-0.5 line-clamp-1" data-testid="draft-titulo">
                                                    {draft.titulo}
                                                </div>
                                            )}
                                            <CardTitle className="font-heading text-base">
                                                {draft.indicador}
                                            </CardTitle>
                                            <CardDescription>{draft.tarea}</CardDescription>
                                        </div>
                                        <NivelEstimadoBadge nivel={draft.nivel_estimado} size="sm" />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-slate-600 line-clamp-3">
                                        {draft.contenido}
                                    </p>
                                    <div className="mt-4 flex items-center justify-between">
                                        <span className="text-xs text-slate-400">
                                            {formatDate(draft.updated_at)}
                                        </span>
                                        <button
                                            className="p-1 rounded hover:bg-slate-100 transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteDraft(draft.id);
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-500" />
                                        </button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <BorradoresEmptyState onCreate={() => {
                        setShowNewDialog(true);
                        trackFunnelEvent('new_draft_clicked', {
                            user_id: profile?.id,
                            last_action: 'empty_state_cta',
                        });
                    }} />
                )}
            </div>
        </Layout>
    );
}
